/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */
import { useEffect, useState, useContext, useMemo, useRef, useCallback } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useTheme } from 'styled-components';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
    getProgressHistory,
    pullProgressFromS3,
    getSetting,
    ProgressHistoryRow,
    ProgressData,
} from '../data';
import { StatusContext } from './StatusContext';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, TimeScale);

const HEATMAP_BUCKETS = 100;
const MIN_INDEX_SLOTS = 20;
const HEATMAP_TOP_PAD = 10;
const HEATMAP_BOTTOM_PAD = 10;
const HEATMAP_LEFT_PAD = 28;
const HEATMAP_RIGHT_PAD = 24;
const HEATMAP_PANEL_H = 520;

function clamp01(v: number): number {
    return Math.max(0, Math.min(1, v));
}

function lerp(a: number, b: number, t: number): number {
    return Math.round(a + ((b - a) * t));
}

function rgbToHex(r: number, g: number, b: number): string {
    const p = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
    return `#${p(r)}${p(g)}${p(b)}`;
}

function heatmapColor(score: number): string {
    const s = Math.pow(clamp01(score), 0.45);
    if (s <= 1 / 3) {
        const t = s / (1 / 3);
        return rgbToHex(lerp(0, 255, t), 0, 0);
    }
    if (s <= 2 / 3) {
        const t = (s - (1 / 3)) / (1 / 3);
        return rgbToHex(255, lerp(0, 212, t), 0);
    }
    const t = (s - (2 / 3)) / (1 / 3);
    return rgbToHex(lerp(255, 57, t), lerp(212, 255, t), lerp(0, 20, t));
}

function safeShortHash(value: any, len = 8): string {
    return String(value || '').slice(0, len);
}

function getMarketingRiskRoutes(data: ProgressData): number {
    if (!Array.isArray(data.supercats)) {
        return 0;
    }
    const allEntry = data.supercats.find((s) => s.key === 'ALL');
    if (allEntry) {
        return allEntry.match_count || 0;
    }
    // Backward-compat fallback for snapshots without ALL.
    return data.supercats
        .filter((s) => s.key !== 'ALL')
        .reduce((sum, s) => sum + (s.match_count || 0), 0);
}

function parseSubmittedTimestampMs(message: string): number | null {
    const m = message.match(/(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)/i);
    if (!m) return null;

    const month = Number(m[1]);
    const day = Number(m[2]);
    const year = Number(m[3]);
    let hour = Number(m[4]);
    const minute = Number(m[5]);
    const second = Number(m[6] || '0');
    const ampm = m[7].toUpperCase();

    if (!Number.isFinite(month) || !Number.isFinite(day) || !Number.isFinite(year) ||
        !Number.isFinite(hour) || !Number.isFinite(minute) || !Number.isFinite(second)) {
        return null;
    }

    if (hour < 1 || hour > 12 || minute < 0 || minute > 59 || second < 0 || second > 59) {
        return null;
    }

    if (ampm === 'PM' && hour !== 12) hour += 12;
    if (ampm === 'AM' && hour === 12) hour = 0;

    const dt = new Date(year, month - 1, day, hour, minute, second, 0);
    const ms = dt.getTime();
    return Number.isFinite(ms) ? ms : null;
}

function synthesizeMessageTimestamps(raw: Array<number | null>, fallback: number[]): number[] {
    const n = raw.length;
    if (n === 0) return [];

    const result: Array<number | null> = [...raw];
    const known = result
        .map((v, i) => (v != null ? i : -1))
        .filter((i) => i >= 0);

    if (known.length === 0) {
        return [...fallback];
    }

    if (known.length === 1) {
        const k = known[0];
        const base = result[k] as number;
        const fbBase = fallback[k] ?? fallback[0] ?? 0;
        for (let i = 0; i < n; i++) {
            if (result[i] == null) {
                const fb = fallback[i] ?? fbBase;
                result[i] = base + (fb - fbBase);
            }
        }
        return result.map((v, i) => v ?? fallback[i] ?? fbBase);
    }

    for (let ki = 0; ki < known.length - 1; ki++) {
        const a = known[ki];
        const b = known[ki + 1];
        const ta = result[a] as number;
        const tb = result[b] as number;
        const gap = b - a;
        for (let i = a + 1; i < b; i++) {
            const t = (i - a) / gap;
            result[i] = ta + ((tb - ta) * t);
        }
    }

    const first = known[0];
    const second = known[1];
    {
        const tf = result[first] as number;
        const ts = result[second] as number;
        const step = (ts - tf) / (second - first);
        for (let i = first - 1; i >= 0; i--) {
            result[i] = tf - (step * (first - i));
        }
    }

    const last = known[known.length - 1];
    const prev = known[known.length - 2];
    {
        const tl = result[last] as number;
        const tp = result[prev] as number;
        const step = (tl - tp) / (last - prev);
        for (let i = last + 1; i < n; i++) {
            result[i] = tl + (step * (i - last));
        }
    }

    return result.map((v, i) => v ?? fallback[i] ?? 0);
}

function DashProgress() {
    const theme = useTheme() as any;
    const statusContext = useContext(StatusContext);
    const [initialLoading, setInitialLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [rows, setRows] = useState<ProgressHistoryRow[]>([]);
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const [hoverDatasetIndex, setHoverDatasetIndex] = useState<number | null>(null);
    const heatmapScrollRef = useRef<HTMLDivElement | null>(null);

    const [startCommit, setStartCommit] = useState<string>('55f178c6');
    const [xMode, setXMode] = useState<'time' | 'index' | 'submittedTime'>('index');
    const [yMode, setYMode] = useState<'zero' | 'auto'>('zero');
    const [hiddenDatasets, setHiddenDatasets] = useState<Set<number>>(new Set());
    const chartRef = useRef<any>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const textStyle = { color: theme?.colors?.textMuted };
    const panelBorder = `1px solid ${theme?.colors?.ctext || 'lime'}`;
    const panelBg = theme?.colors?.logBg || '#000';
    const cardBg = theme?.colors?.headerBg || '#1d2730';
    const textColor = theme?.colors?.ctext || 'lime';
    const infoColor = theme?.colors?.info || '#0dcaf0';
    const mutedColor = theme?.colors?.textMuted || '#888';
    const cardRadius = '0.375rem';

    const load = useCallback(async (isRefresh = false) => {
        if (isRefresh) {
            setRefreshing(true);
        } else {
            setInitialLoading(true);
        }
        try {
            const autoRefresh = await getSetting('/display/progressAutoRefresh').catch(() => 'true');
            if (autoRefresh !== 'false') {
                try { await pullProgressFromS3(); } catch (_e) { /* non-fatal */ }
            }
            setRows(await getProgressHistory());
        } catch (err: any) {
            statusContext.postError(`Error loading progress data: ${err.message}`);
        } finally {
            setInitialLoading(false);
            setRefreshing(false);
        }
    }, [statusContext]);

    const isFirstLoad = useRef(true);
    useEffect(() => {
        const first = isFirstLoad.current;
        isFirstLoad.current = false;
        load(!first ? true : false);
    }, [load, refreshKey]);

    const sorted = useMemo(() => [...rows].sort((a, b) => a.date.localeCompare(b.date)), [rows]);

    const displaySorted = useMemo(() => {
        if (!startCommit) return sorted;
        const idx = sorted.findIndex((r) => r.hash.startsWith(startCommit));
        return idx >= 0 ? sorted.slice(idx) : sorted;
    }, [sorted, startCommit]);

    const parsed = useMemo(() => displaySorted.map((row) => {
        let data: ProgressData | null = null;
        try {
            data = JSON.parse(row.data) as ProgressData;
        } catch (_e) {
            data = null;
        }
        let tags: string[] = [];
        try {
            const parsedTags = JSON.parse(row.git_tags || '[]') as string[];
            tags = Array.isArray(parsedTags) ? parsedTags : [];
        } catch (_e) {
            tags = [];
        }
        const releaseTag = tags.find((t) => t.startsWith('release/')) || '';
        const bucketScores = Array.from({ length: HEATMAP_BUCKETS }, (_, i) => {
            const v = data?.fingerprint_space_scores?.[i];
            return typeof v === 'number' && Number.isFinite(v) ? clamp01(v) : 0;
        });
        return {
            row,
            data,
            tags,
            releaseTag,
            bucketScores,
        };
    }), [displaySorted]);

    const timestamps = displaySorted.map((r) => new Date(r.date).getTime());
    const submittedMessageTimestamps = useMemo(() => {
        const raw = displaySorted.map((r) => parseSubmittedTimestampMs(r.message));
        return synthesizeMessageTimestamps(raw, timestamps);
    }, [displaySorted, timestamps]);

    const fingerprintCounts = parsed.map((p) => p.data?.total_fingerprints || 0);
    const marketingRiskRouteCounts = parsed.map((p) => p.data ? getMarketingRiskRoutes(p.data) : 0);
    const engineeringMatchCounts = parsed.map((p) => p.data?.total_matches || 0);

    const allCountSeries = [fingerprintCounts, marketingRiskRouteCounts, engineeringMatchCounts];
    const isDatasetVisible = (i: number) => {
        const chart = chartRef.current;
        if (chart && typeof chart.isDatasetVisible === 'function') {
            return chart.isDatasetVisible(i);
        }
        return !hiddenDatasets.has(i);
    };
    const visibleValues = allCountSeries
        .flatMap((series, i) => isDatasetVisible(i) ? series : [])
        .filter((v) => v > 0);
    const displayedMin = visibleValues.length > 0 ? Math.min(...visibleValues) : 0;
    const displayedMax = visibleValues.length > 0 ? Math.max(...visibleValues) : 1;
    const yAutoMin = Math.max(0, displayedMin - (displayedMax - displayedMin) * 0.08);

    const activeIndex = hoverIndex != null ? hoverIndex : Math.max(0, displaySorted.length - 1);
    const activeRow = displaySorted[activeIndex];
    const activeTags = parsed[activeIndex]?.tags || [];

    const xIsIndex = xMode === 'index';
    const xTimeValues = xMode === 'submittedTime' ? submittedMessageTimestamps : timestamps;
    const xWindowStart = xTimeValues[Math.max(0, xTimeValues.length - 10)] ?? 0;
    const xWindowEnd = xTimeValues[xTimeValues.length - 1] ?? 0;
    const xPad = xTimeValues.length > 1 ? (xWindowEnd - xWindowStart) * 0.04 : 86400000;
    const xIndexMax = Math.max(parsed.length - 1, MIN_INDEX_SLOTS - 1);

    useEffect(() => {
        const el = heatmapScrollRef.current;
        if (!el) return;
        el.scrollLeft = el.scrollWidth;
    }, [parsed.length]);

    if (initialLoading) {
        return (
            <Container fluid className="pt-3 px-4">
                <p style={textStyle}>Loading progress data...</p>
            </Container>
        );
    }

    if (rows.length === 0) {
        return (
            <Container fluid className="pt-3 px-4">
                <p style={textStyle}>No progress history available. Use Tools &#8594; Refresh Progress Statistics to populate data.</p>
            </Container>
        );
    }

    const xValues = xIsIndex ? parsed.map((_, i) => i) : xTimeValues;

    const chartData = {
        datasets: [
            {
                label: 'Fingerprints',
                data: xValues.map((x, i) => ({ x, y: fingerprintCounts[i] })),
                borderColor: theme?.colors?.ctext || 'lime',
                backgroundColor: 'rgba(0, 255, 0, 0.12)',
                tension: 0.2,
                pointRadius: 3,
                hitRadius: 10,
            },
            {
                label: 'Risk Routes',
                data: xValues.map((x, i) => ({ x, y: marketingRiskRouteCounts[i] })),
                borderColor: theme?.colors?.info || '#0dcaf0',
                backgroundColor: 'rgba(13, 202, 240, 0.12)',
                tension: 0.2,
                pointRadius: 3,
                hitRadius: 10,
            },
            {
                label: 'Matches',
                data: xValues.map((x, i) => ({ x, y: engineeringMatchCounts[i] })),
                borderColor: theme?.colors?.warning || '#ffc107',
                backgroundColor: 'rgba(255, 193, 7, 0.12)',
                tension: 0.2,
                pointRadius: 3,
                hitRadius: 10,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                left: 15,
                right: 0,
                top: 0,
                bottom: 0,
            },
        },
        interaction: { mode: 'index' as const, intersect: false },
        onHover: (event: any, elements: any[]) => {
            if (!elements || elements.length === 0) {
                setHoverIndex(null);
                setHoverDatasetIndex(null);
                return;
            }
            const idx = elements[0]?.index;
            setHoverIndex(typeof idx === 'number' ? idx : null);
            const cursorY = event?.y;
            if (typeof cursorY === 'number') {
                let minDist = Infinity;
                let closest: number | null = null;
                elements.forEach((el: any) => {
                    const dist = Math.abs((el.element?.y ?? Infinity) - cursorY);
                    if (dist < minDist) { minDist = dist; closest = el.datasetIndex; }
                });
                setHoverDatasetIndex(closest);
            }
        },
        plugins: {
            legend: {
                position: 'top' as const,
                labels: {
                    color: textColor,
                },
                onClick: (_e: any, legendItem: any) => {
                    const index = Number(legendItem.datasetIndex);
                    if (!Number.isFinite(index)) return;
                    const chart = chartRef.current;
                    if (!chart) return;
                    chart.setDatasetVisibility(index, !chart.isDatasetVisible(index));
                    chart.update();
                    setHiddenDatasets((prev) => {
                        const next = new Set(prev);
                        if (next.has(index)) next.delete(index); else next.add(index);
                        return next;
                    });
                },
            },
            tooltip: { enabled: false },
        },
        scales: {
            x: xIsIndex ? {
                type: 'linear' as const,
                min: 0,
                max: xIndexMax,
                ticks: {
                    stepSize: 1,
                    autoSkip: true,
                    maxTicksLimit: 12,
                    maxRotation: 45,
                    minRotation: 0,
                    padding: 4,
                    color: textColor,
                    callback: (value: any) => {
                        const i = Math.round(value);
                        const p = parsed[i];
                        if (!p) return '';
                        return p.releaseTag ? p.releaseTag.replace('release/', 'r') : safeShortHash(p.row?.hash, 7);
                    },
                },
                grid: {
                    color: 'rgba(0, 255, 0, 0.12)',
                },
            } : {
                type: 'linear' as const,
                min: xWindowStart - xPad,
                max: xWindowEnd + xPad,
                ticks: {
                    autoSkip: true,
                    maxTicksLimit: 10,
                    maxRotation: 0,
                    minRotation: 0,
                    padding: 6,
                    color: textColor,
                    callback: (value: any) => {
                        const d = new Date(value);
                        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
                    },
                },
                grid: {
                    color: 'rgba(0, 255, 0, 0.12)',
                },
            },
            y: {
                type: 'linear' as const,
                ...(yMode === 'zero' ? { beginAtZero: true } : { min: yAutoMin }),
                title: {
                    display: false,
                    text: 'Count',
                    color: textColor,
                },
                grid: {
                    color: 'rgba(0, 255, 0, 0.12)',
                },
                ticks: {
                    padding: 8,
                    color: textColor,
                },
            },
        },
    };

    const heatmapBodyH = HEATMAP_PANEL_H - HEATMAP_TOP_PAD - HEATMAP_BOTTOM_PAD;
    const heatmapCellH = Math.max(3, Math.floor(heatmapBodyH / HEATMAP_BUCKETS));
    const heatmapCellW = Math.max(heatmapCellH * 2, 8);
    const heatmapW = parsed.length * heatmapCellW;
    const heatmapH = HEATMAP_BUCKETS * heatmapCellH;
    const heatmapSvgW = HEATMAP_LEFT_PAD + heatmapW + HEATMAP_RIGHT_PAD;
    const heatmapSvgH = HEATMAP_PANEL_H;
    const heatmapHoverX = hoverIndex != null ? HEATMAP_LEFT_PAD + (hoverIndex * heatmapCellW) : null;

    const heatCells: React.ReactNode[] = [];
    for (let col = 0; col < parsed.length; col++) {
        const scores = parsed[col].bucketScores;
        for (let row = 0; row < HEATMAP_BUCKETS; row++) {
            const score = scores[row] ?? 0;
            const x = HEATMAP_LEFT_PAD + (col * heatmapCellW);
            const y = HEATMAP_TOP_PAD + (HEATMAP_BUCKETS - 1 - row) * heatmapCellH;
            heatCells.push(
                <rect
                    key={`h-${col}-${row}`}
                    x={x}
                    y={y}
                    width={heatmapCellW - 1}
                    height={heatmapCellH - 1}
                    fill={heatmapColor(score)}
                    opacity={0.95}
                />
            );
        }
    }

    return (
        <Container fluid className="pt-3 px-4" style={{ backgroundColor: panelBg, minHeight: '100%' }}>
            <Row className="align-items-center mb-3">
                <Col>
                    <h4 style={{ color: textColor, fontFamily: 'monospace', letterSpacing: '0.04em', marginBottom: 0 }}>Discovery and Validation Progress</h4>
                </Col>
                <Col xs="auto" className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                    <label style={{ color: mutedColor, fontFamily: 'monospace', fontSize: '0.82rem', whiteSpace: 'nowrap', marginBottom: 0 }}>X axis:</label>
                    <select
                        value={xMode}
                        onChange={(e) => setXMode(e.target.value as 'time' | 'index' | 'submittedTime')}
                        style={{
                            backgroundColor: cardBg,
                            color: textColor,
                            border: `1px solid ${mutedColor}`,
                            borderRadius: '0.25rem',
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            padding: '2px 6px',
                        }}
                    >
                        <option value="time">timestamp</option>
                        <option value="submittedTime">submitted timestamp</option>
                        <option value="index">commit index</option>
                    </select>
                </Col>
                <Col xs="auto" className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                    <label style={{ color: mutedColor, fontFamily: 'monospace', fontSize: '0.82rem', whiteSpace: 'nowrap', marginBottom: 0 }}>Start commit:</label>
                    <select
                        value={startCommit}
                        onChange={(e) => setStartCommit(e.target.value)}
                        style={{
                            backgroundColor: cardBg,
                            color: textColor,
                            border: `1px solid ${mutedColor}`,
                            borderRadius: '0.25rem',
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            padding: '2px 6px',
                            maxWidth: '28rem',
                        }}
                    >
                        <option value="">— all commits —</option>
                        {sorted.map((r) => {
                            let tags: string[] = [];
                            try { tags = JSON.parse(r.git_tags || '[]') as string[]; } catch (_e) { /* ignore */ }
                            const releaseTag = tags.find((t) => t.startsWith('release/')) || '';
                            const shortHash = safeShortHash(r.hash, 8);
                            const label = releaseTag
                                ? `${shortHash}  [${releaseTag}]  ${r.message}`
                                : `${shortHash}  ${r.message}`;
                            return <option key={r.hash} value={shortHash}>{label}</option>;
                        })}
                    </select>
                </Col>
                <Col xs="auto">
                    <button
                        onClick={() => setRefreshKey((k) => k + 1)}
                        title="Refresh progress data"
                        aria-label="Refresh progress data"
                        style={{
                            background: 'none',
                            border: 'none',
                            color: mutedColor,
                            fontFamily: 'monospace',
                            fontSize: '2rem',
                            padding: '0 2px',
                            cursor: 'pointer',
                            lineHeight: 1,
                        }}
                    >&#x21bb;</button>
                </Col>
                <Col xs="auto">
                    <div style={{ color: '#8b1e1e', fontFamily: 'monospace', fontWeight: 700, fontSize: 'calc(1.275rem + 0.3vw)', lineHeight: 1.2, letterSpacing: '0.04em' }}>CONFIDENTIAL</div>
                </Col>
            </Row>
            <Row className="align-items-stretch">
                <Col lg={9} md={8}>
                    <div className="rounded" style={{ position: 'relative', height: `${HEATMAP_PANEL_H}px`, backgroundColor: panelBg, border: panelBorder, borderRadius: cardRadius, padding: '8px 10px 4px 6px' }}>
                        {refreshing && (
                            <div style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'rgba(0,0,0,0.45)',
                                borderRadius: cardRadius,
                                zIndex: 20,
                                pointerEvents: 'none',
                            }}>
                                <span style={{
                                    color: textColor,
                                    fontSize: '2.4rem',
                                    lineHeight: 1,
                                    animation: 'spin 1s linear infinite',
                                    display: 'inline-block',
                                }}>&#x21bb;</span>
                                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                            </div>
                        )}
                        <button
                            onClick={() => setYMode((m) => m === 'zero' ? 'auto' : 'zero')}
                            title={yMode === 'zero' ? 'Y axis starts at 0 - click for auto-min' : 'Y axis starts near min - click for zero'}
                            aria-label="Toggle Y axis baseline"
                            style={{
                                position: 'absolute',
                                left: '-.75em',
                                top: '50%',
                                transform: 'translateY(-50%) rotate(-90deg)',
                                transformOrigin: 'center',
                                background: 'none',
                                border: 'none',
                                color: textColor,
                                fontFamily: 'monospace',
                                fontSize: '1.0rem',
                                padding: '0 6px',
                                cursor: 'pointer',
                                zIndex: 2,
                                opacity: 0.9,
                            }}
                        >Count</button>
                        <Line ref={chartRef} data={chartData} options={options} />
                    </div>
                </Col>
                <Col lg={3} md={4}>
                    <div
                        className="rounded"
                        ref={heatmapScrollRef}
                        style={{
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            height: `${HEATMAP_PANEL_H}px`,
                            border: panelBorder,
                            background: panelBg,
                            borderRadius: cardRadius,
                        }}
                    >
                        <div
                            style={{
                                width: `${Math.max(heatmapSvgW, 1)}px`,
                                minWidth: '100%',
                                height: `${heatmapSvgH}px`,
                                position: 'relative',
                                backgroundColor: panelBg,
                            }}
                            onMouseMove={(e) => {
                                const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                const y = e.clientY - rect.top;
                                const inX = x >= HEATMAP_LEFT_PAD && x < (HEATMAP_LEFT_PAD + heatmapW);
                                const inY = y >= HEATMAP_TOP_PAD && y < (HEATMAP_TOP_PAD + heatmapH);
                                if (!inX || !inY) {
                                    setHoverIndex(null);
                                    return;
                                }
                                const idx = Math.floor((x - HEATMAP_LEFT_PAD) / heatmapCellW);
                                setHoverIndex(Math.max(0, Math.min(parsed.length - 1, idx)));
                            }}
                            onMouseLeave={() => setHoverIndex(null)}
                        >
                            <svg width={Math.max(heatmapSvgW, 1)} height={heatmapSvgH} viewBox={`0 0 ${Math.max(heatmapSvgW, 1)} ${heatmapSvgH}`} style={{ display: 'block', background: panelBg }}>
                                <rect
                                    x={HEATMAP_LEFT_PAD}
                                    y={HEATMAP_TOP_PAD}
                                    width={heatmapW}
                                    height={heatmapH}
                                    fill={panelBg}
                                />
                                {heatCells}
                                {heatmapHoverX != null && (
                                    <rect
                                        x={heatmapHoverX}
                                        y={HEATMAP_TOP_PAD}
                                        width={heatmapCellW - 1}
                                        height={heatmapH}
                                        fill="rgba(255,255,255,0.14)"
                                        stroke="rgba(255,255,255,0.5)"
                                        strokeWidth={1}
                                    />
                                )}
                                <text
                                    x={12}
                                    y={HEATMAP_TOP_PAD + Math.floor(heatmapH / 2)}
                                    transform={`rotate(-90, 12, ${HEATMAP_TOP_PAD + Math.floor(heatmapH / 2)})`}
                                    textAnchor="middle"
                                    fontSize={11}
                                    fill={textColor}
                                >
                                    Fingerprint IDs
                                </text>
                            </svg>
                        </div>
                    </div>
                </Col>
            </Row>
            <Row className="mt-3 g-3">
                <Col sm={6} lg={4}>
                    <div
                        className="h-100 rounded"
                        style={{
                            backgroundColor: cardBg,
                            border: panelBorder,
                            borderRadius: cardRadius,
                            color: textColor,
                            fontSize: '0.92rem',
                            padding: '0.9rem 1rem',
                        }}
                    >
                        <div className="mb-1" style={{ color: infoColor, fontFamily: 'monospace' }}>Commit</div>
                        <div className="mb-2" style={{ lineHeight: 1.35 }}>{activeRow?.message || 'n/a'}</div>
                        <div className="mb-1" style={{ color: infoColor, fontFamily: 'monospace' }}>Date</div>
                        <div>{activeRow ? new Date(activeRow.date).toLocaleString() : 'n/a'}</div>
                        <div className="small mt-3" style={{ color: mutedColor }}>{displaySorted.length} of {sorted.length} commits shown</div>
                    </div>
                </Col>
                <Col sm={6} lg={4}>
                    <div
                        className="h-100 rounded"
                        style={{
                            backgroundColor: cardBg,
                            border: panelBorder,
                            borderRadius: cardRadius,
                            color: textColor,
                            fontSize: '0.92rem',
                            padding: '0.9rem 1rem',
                        }}
                    >
                        <div className="mb-1" style={{ color: infoColor, fontFamily: 'monospace' }}>Hash</div>
                        <div className="mb-2" style={{ wordBreak: 'break-all' }}>{activeRow?.hash || 'n/a'}</div>
                        <div className="mb-1" style={{ color: infoColor, fontFamily: 'monospace' }}>Tags</div>
                        <div>{activeTags.length > 0 ? activeTags.join(', ') : 'none'}</div>
                    </div>
                </Col>
                <Col sm={12} lg={4}>
                    <div
                        className="h-100 rounded"
                        style={{
                            backgroundColor: cardBg,
                            border: panelBorder,
                            borderRadius: cardRadius,
                            color: textColor,
                            fontSize: '0.92rem',
                            padding: '0.9rem 1rem',
                        }}
                    >
                        <div className="mb-1" style={{ color: infoColor, fontFamily: 'monospace' }}>Fingerprints</div>
                        <div className="mb-2" style={{ minHeight: '1.4em' }}><span style={{ display: 'inline-block', transform: hoverDatasetIndex === 0 ? 'scale(2)' : 'scale(1)', transformOrigin: 'left center', transition: 'transform 0.12s ease' }}>{fingerprintCounts[activeIndex]?.toLocaleString?.() ?? '0'}</span></div>
                        <div className="mb-1" style={{ color: infoColor, fontFamily: 'monospace' }}>Risk Routes</div>
                        <div className="mb-2" style={{ minHeight: '1.4em' }}><span style={{ display: 'inline-block', transform: hoverDatasetIndex === 1 ? 'scale(2)' : 'scale(1)', transformOrigin: 'left center', transition: 'transform 0.12s ease' }}>{marketingRiskRouteCounts[activeIndex]?.toLocaleString?.() ?? '0'}</span></div>
                        <div className="mb-1" style={{ color: infoColor, fontFamily: 'monospace' }}>Matches</div>
                        <div style={{ minHeight: '1.4em' }}><span style={{ display: 'inline-block', transform: hoverDatasetIndex === 2 ? 'scale(2)' : 'scale(1)', transformOrigin: 'left center', transition: 'transform 0.12s ease' }}>{engineeringMatchCounts[activeIndex]?.toLocaleString?.() ?? '0'}</span></div>
                    </div>
                </Col>
            </Row>
            <Row className="mt-3">
                <Col>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'monospace', fontSize: '0.8rem', color: textColor }}>
                            <thead>
                                <tr style={{ borderBottom: `1px solid ${textColor}`, color: mutedColor }}>
                                    <th style={{ padding: '2px 10px 4px 0', fontWeight: 400, whiteSpace: 'nowrap' }}>Date</th>
                                    <th style={{ padding: '2px 10px 4px 0', fontWeight: 400, whiteSpace: 'nowrap' }}>Hash</th>
                                    <th style={{ padding: '2px 10px 4px 0', fontWeight: 400 }}>Message</th>
                                    <th style={{ padding: '2px 10px 4px 0', fontWeight: 400, whiteSpace: 'nowrap', textAlign: 'right' }}>Fingerprints</th>
                                    <th style={{ padding: '2px 10px 4px 0', fontWeight: 400, whiteSpace: 'nowrap', textAlign: 'right' }}>Risk Routes</th>
                                    <th style={{ padding: '2px 0 4px 0', fontWeight: 400, whiteSpace: 'nowrap', textAlign: 'right' }}>Matches</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...parsed].reverse().map((p, i) => {
                                    const isActive = activeIndex === (parsed.length - 1 - i);
                                    return (
                                        <tr
                                            key={p.row.hash}
                                            style={{ borderBottom: `1px solid rgba(128,128,128,0.15)`, backgroundColor: isActive ? 'rgba(255,255,255,0.04)' : 'transparent' }}
                                            onMouseEnter={() => setHoverIndex(parsed.length - 1 - i)}
                                            onMouseLeave={() => setHoverIndex(null)}
                                        >
                                            <td style={{ padding: '2px 10px 2px 0', whiteSpace: 'nowrap', color: mutedColor }}>{new Date(p.row.date).toLocaleString()}</td>
                                            <td style={{ padding: '2px 10px 2px 0', whiteSpace: 'nowrap', color: infoColor }}>{safeShortHash(p.row?.hash, 8)}{p.releaseTag ? <span style={{ marginLeft: 6, color: textColor }}>{p.releaseTag}</span> : null}</td>
                                            <td style={{ padding: '2px 10px 2px 0' }}>{p.row.message}</td>
                                            <td style={{ padding: '2px 10px 2px 0', textAlign: 'right', whiteSpace: 'nowrap' }}>{p.data?.total_fingerprints?.toLocaleString?.() ?? '—'}</td>
                                            <td style={{ padding: '2px 10px 2px 0', textAlign: 'right', whiteSpace: 'nowrap' }}>{p.data ? getMarketingRiskRoutes(p.data).toLocaleString() : '—'}</td>
                                            <td style={{ padding: '2px 0 2px 0', textAlign: 'right', whiteSpace: 'nowrap' }}>{p.data?.total_matches?.toLocaleString?.() ?? '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Col>
            </Row>
        </Container>
    );
}

export default DashProgress;
